// SlashCommandDropdown.tsx - Slack-style content selector for "/" command
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ContentItem } from '../../store/slices/contentSlice';
import logger from '../../utils/logger';

interface SlashCommandDropdownProps {
  isOpen: boolean;
  filteredContent: ContentItem[];
  selectedIndex: number;
  searchTerm: string;
  onSelect: (content: ContentItem) => void;
  onClose: () => void;
  loading: boolean;
  anchorElement?: HTMLElement | null; // Reference to the textarea for positioning
}

const SlashCommandDropdown: React.FC<SlashCommandDropdownProps> = ({
  isOpen,
  filteredContent,
  selectedIndex,
  searchTerm,
  onSelect,
  onClose,
  loading,
  anchorElement
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  // Calculate position based on anchor element
  useEffect(() => {
    if (isOpen && anchorElement) {
      const rect = anchorElement.getBoundingClientRect();

      // Position above the textarea with proper spacing
      const dropdownTop = rect.top - 20; // 20px gap above textarea
      const dropdownLeft = rect.left;
      const dropdownWidth = Math.max(rect.width, 400); // Minimum 400px width

      setPosition({
        top: dropdownTop,
        left: dropdownLeft,
        width: dropdownWidth
      });
    }
  }, [isOpen, anchorElement]);

  // Log state changes
  useEffect(() => {
    if (isOpen) {
      logger.log('Slash command dropdown opened with', filteredContent.length, 'items');
    }
  }, [isOpen, filteredContent.length]);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (dropdownRef.current && selectedIndex >= 0) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) {
    return null;
  }

  // Helper functions - defined first before use
  const renderContentIcon = (type: string) => {
    switch (type) {
      case 'message':
        return '💬';
      case 'file':
        return '📁';
      case 'page':
        return '📄';
      default:
        return '📝';
    }
  };

  const renderContentPreview = (content: ContentItem): string => {
    const preview =
      content.details?.message ||
      content.details?.description ||
      content.details?.title ||
      'No preview available';

    // Truncate to 60 characters
    return preview.length > 60 ? preview.substring(0, 60) + '...' : preview;
  };

  const renderContentCard = (content: ContentItem, index: number) => {
    const isSelected = index === selectedIndex;
    const title = content.name || content.details?.title || 'Untitled';

    return (
      <div
        key={content._id}
        onClick={() => {
          logger.log('📌 Content selected from slash command:', content._id);
          onSelect(content);
        }}
        style={{
          padding: '12px 16px',
          backgroundColor: isSelected ? '#f0f9ff' : 'white',
          borderLeft: isSelected ? '3px solid #0084ff' : '3px solid transparent',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          borderBottom: '1px solid #f1f3f5',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'white';
          }
        }}
      >
        {/* Icon */}
        <div style={{ fontSize: '20px', lineHeight: '1', flexShrink: 0 }}>
          {renderContentIcon(content.type)}
        </div>

        {/* Content Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a1a1a',
              marginBottom: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: '12px',
              color: '#6c757d',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {renderContentPreview(content)}
          </div>
        </div>

        {/* Type Badge */}
        <div
          style={{
            fontSize: '10px',
            color: '#059669',
            backgroundColor: '#ecfdf5',
            padding: '2px 6px',
            borderRadius: '4px',
            textTransform: 'uppercase',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {content.type}
        </div>
      </div>
    );
  };

  const renderDropdownUI = () => {
    return (
      <>
        {/* Header */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e9ecef',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>
            {searchTerm ? `Search: "${searchTerm}"` : 'Select Content'}
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d' }}>
            {loading ? 'Loading...' : `${filteredContent.length} items`}
          </div>
        </div>

        {/* Content List */}
        <div>
          {loading ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '13px',
              }}
            >
              <i className="ri-loader-4-line" style={{
                fontSize: '20px',
                animation: 'spin 1s linear infinite',
                display: 'block',
                marginBottom: '8px'
              }}></i>
              Loading content...
            </div>
          ) : filteredContent.length === 0 ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '13px',
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
              <div style={{ fontWeight: 500, marginBottom: '4px' }}>No content found</div>
              <div style={{ fontSize: '12px' }}>
                {searchTerm ? 'Try a different search term' : 'No content available'}
              </div>
            </div>
          ) : (
            filteredContent.map((content, index) => renderContentCard(content, index))
          )}
        </div>

        {/* Footer - Keyboard Hints */}
        {!loading && filteredContent.length > 0 && (
          <div
            style={{
              padding: '8px 16px',
              borderTop: '1px solid #e9ecef',
              backgroundColor: '#f8f9fa',
              borderRadius: '0 0 12px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '11px',
              color: '#6c757d',
              position: 'sticky',
              bottom: 0,
            }}
          >
            <span>↑↓ Navigate</span>
            <span>•</span>
            <span>↵ Select</span>
            <span>•</span>
            <span>Esc Close</span>
          </div>
        )}
      </>
    );
  };

  // Render dropdown content
  const dropdownContent = (
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: '400px',
        transform: 'translateY(-100%)',
        overflowY: 'auto',
        backgroundColor: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.12), 0 -4px 12px rgba(0, 0, 0, 0.08)',
        zIndex: 99999,
      }}
      ref={dropdownRef}
    >
      {renderDropdownUI()}
    </div>
  );

  // Use portal to render at body level, breaking out of stacking context
  return ReactDOM.createPortal(dropdownContent, document.body);
};

export default SlashCommandDropdown;
